// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { MUST_SEARCH_AT_LEAST_ONE_VALUE } from 'errors'
import { ifEntityGetId } from 'helpers/reduxFormFunctions'
import { PathStatusIdEnum } from 'config/enums'

import {
  assembleQuery,
  fetchPathsByQuery,
  makePaginationParams,
} from 'helpers/api'

// these fields are arrays of objects in redux-form.
// for submit handling, we want to reduce them to a string of their ids.
const joinIds = [
  'bandId',
]

// fields which are handled by redux-form but we don't want to send to the API
const omitThese = [
  'pagination',
]

const setContactId = (k, v, a) => {
  const contactId = v.map(d => d.contactId).join(',')

  if (contactId) {
    a[k] = contactId
  }
}

const prepData = data => Object.keys(data).reduce((a, k) => {
  const v = data[k]

  // these are values that the API doesn't care about
  if (~omitThese.indexOf(k)) {
    return a
  }

  // these are multi selects.
  // this makes a string of the selected entities' ids
  if (~joinIds.indexOf(k)) {
    a[k] = v.map(d => ifEntityGetId(d)).join(',')
    return a
  }

  // take the user id from the create user field.
  if (k === 'createUserId') {
    a.projectCreateUserId = v.userId
  } else if (k === 'contactId') {
    setContactId(k, v, a)
  } else {
    // finally, any other value is just passed through.
    a[k] = v
  }

  return a
}, {})

export const makeFormQuery = formValues => {
  const searchBasis = []

  let hasValue = false

  // first create a new object which is a reduced version of the original form's data.
  const midData = prepData(formValues)

  // adjust this as necessary for each search form
  Object.keys(midData).forEach(field => {
    const value = midData[field]
    const operator = (
      field === 'projectName'
        ? 'LIKE'
        : 'EQ'
    )

    if (value) {
      hasValue = true
      searchBasis.push({
        value,
        field,
        operator,
      })
    }
  })

  // form must have at least one value
  // throws error message if false
  if (!hasValue) {
    throw new Error(MUST_SEARCH_AT_LEAST_ONE_VALUE)
  }

  // creates a 2d array which searches:
  // searchBasis AND NOT Finalized paths
  // OR
  // searchBasis AND Finalized paths which are also Pending True
  const search = [[
    ...searchBasis,
    {
      field: 'pathStatusId',
      operator: 'NOT',
      value: PathStatusIdEnum.FINALIZED,
    }], [
    ...searchBasis,
    {
      field: 'pathStatusId',
      operator: 'EQ',
      value: PathStatusIdEnum.FINALIZED,
    }, {
      field: 'pending',
      operator: 'EQ',
      value: true,
    },
  ]]

  return {
    field: 'search',
    value: search,
  }
}

const fetchData = ({ formProps, formValues, submittedFormIsPristine }) => {
  // if submitSucceeded was true, that means we want to make sure to persist the
  // defined page value for the query. Otherwise, reset the page.
  const page = (
    formValues.pagination && submittedFormIsPristine(formProps.form)
      ? +formValues.pagination.page
      : 0
  )

  // size of results set in API response - default to 200 count
  const size = 200

  const paginationParams = makePaginationParams({
    page,
    size,
  })

  const query = [
    makeFormQuery(formValues, formProps),
    ...paginationParams,
  ]

  return fetchPathsByQuery(assembleQuery(query))
}

export default fetchData
